import { useEffect, useState } from 'react';
import Head from 'next/head';
import { gql } from '@apollo/client';
import { useRouter } from 'next/router';
import Skeleton from 'react-loading-skeleton';
import {
  client,
  getFirstNumberOfPosts,
  getPageSEOByURI
} from '../apollo-client';
import { useAuth0User } from '@hooks/useAuth0User';

import { useLeagueContext } from '@hooks/useLeagueContext';
import Layout from '@components/layout';
import { getYoastHeadSEO, isPaidMember } from '@lib/utils';
import HomepageHero from '@components/homepage/homepage-hero';
import ReviewsCarousel from '@components/reviews-carousel';
import FeaturedArticle from '@components/featured-article';
import { LatestArticles } from '@components/homepage/latest-articles';
import ToolsShowcase from '@components/homepage/tools-showcase';
import VideoAdvert from '@components/video-advert';
import {
  getHomepageSettings,
  getLatestListingCards,
  getMarketingCopy
} from '@lib/sanity';
import type { MarketingCopy } from '@lib/sanity';
import { reportIsrRegenerationFailure } from '@lib/isr/report-regeneration-failure';
import { homepageToolsData } from '@components/homepage/tools-showcase/utils';
import {
  HOMEPAGE_SOURCE_LIMIT,
  buildHomepageListing
} from '@lib/homepage/build';
import type { HomepageListing } from '@lib/homepage/build';
import { cardLabelFor } from '@lib/homepage/tabs';
import type { HomepageTab } from '@lib/homepage/tabs';

const REVALIDATE_SECONDS = 300;

type HomeProps = HomepageListing & {
  heading: string;
  tabs: HomepageTab[];
  cardCount: number;
  page: any;
  marketingCopy: MarketingCopy;
};

const Home = ({
  lead,
  items,
  heading,
  tabs,
  cardCount,
  page,
  marketingCopy
}: HomeProps) => {
  const { context } = useLeagueContext();

  const nextGameweek = context?.nextGameweek?.number ?? null;
  const [isPayingMember, setIsPayingMember] = useState(false);
  const { user, isLoading } = useAuth0User();

  useEffect(() => {
    const hasPaidMembership = isPaidMember(user);

    if (hasPaidMembership) {
      setIsPayingMember(true);
    }
  }, [user]);

  const router = useRouter();
  const pathname = router.pathname;
  const yoastHead = getYoastHeadSEO(page?.seo?.fullHead, pathname);

  if (isLoading) {
    // This branch is what gets statically generated (Auth0 is always loading
    // server-side), so the SEO head must render here or crawlers never see it.
    return (
      <>
        <Head>{yoastHead}</Head>
        <div className="py-10 h-[95vh]">
          <Skeleton count={12} width={'100%'} />
        </div>
      </>
    );
  }

  return (
    <>
      <div>
        <Head>{yoastHead}</Head>
        {!isPayingMember && (
          <>
            <div className="container mx-auto px-5">
              <HomepageHero copy={marketingCopy.homepage} />
            </div>
            <ReviewsCarousel />
            <ToolsShowcase
              toolsData={homepageToolsData}
              imagePath={'/images/homepage/tools-showcase.png'}
            />
            <VideoAdvert variant="image" />
          </>
        )}

        <FeaturedArticle
          nextGameweek={nextGameweek}
          item={lead}
          label={lead ? cardLabelFor(lead, tabs) : undefined}
        />
        <Layout hasRightSidebar={isPayingMember} content={false}>
          <LatestArticles
            heading={heading}
            items={items}
            tabs={tabs}
            cardCount={cardCount}
          />
        </Layout>
      </div>
    </>
  );
};

export const getStaticProps = async () => {
  try {
    // network-only: the module-level Apollo client persists across ISR
    // revalidations in a warm lambda, so cache-first would pin these
    // queries to whatever the instance fetched first.
    const [postsQuery, pageQuery, sanityCards, settings, marketingCopy] =
      await Promise.all([
        client.query({
          query: gql`
            ${getFirstNumberOfPosts(HOMEPAGE_SOURCE_LIMIT)}
          `,
          fetchPolicy: 'network-only'
        }),
        client.query({
          query: gql`
            ${getPageSEOByURI('/')}
          `,
          fetchPolicy: 'network-only'
        }),
        getLatestListingCards(HOMEPAGE_SOURCE_LIMIT),
        getHomepageSettings(),
        getMarketingCopy()
      ]);

    const listing = buildHomepageListing({
      wordPressNodes: postsQuery.data.posts.nodes,
      sanityCards,
      leadStory: settings.leadStory
    });

    return {
      props: {
        ...listing,
        heading: settings.heading,
        tabs: settings.tabs,
        cardCount: settings.cardCount,
        page: pageQuery.data.pageBy,
        marketingCopy
      },
      revalidate: REVALIDATE_SECONDS
    };
  } catch (error) {
    return reportIsrRegenerationFailure('/', error);
  }
};

export default Home;
